import React, { useState, useEffect } from 'react';
import traApi from '../services/traApi';
import * as XLSX from 'xlsx';

// Type definitions with proper field names matching backend
interface Ingredient {
  id: number;
  inci_name: string;
  cas_number?: string;
  function?: string;
  metadata_json?: string;
  noael_mg_kg_day?: number;
  dermal_absorption_pct?: number;
  sccs_ref?: string;
  allergenicity?: string;
  toxicological_class?: string;
  created_at?: string;
  updated_at?: string;
}

interface Product {
  id: number;
  name: string;
  product_type?: string;
  intended_use?: string;
  target_audience?: string;
  safety_data_available?: boolean;
  created_at?: string;
}

interface FormulaItem {
  ingredient_id: number;
  concentration_pct: number;
  temp_name?: string;
  ingredient_name?: string;
  inci_name?: string;
  cas_number?: string;
  function?: string;
  override_da_pct?: number | null;
  override_noael_mg_kg_day?: number | null;
}

interface Formula {
  id: number;
  name: string;
  product_id?: number;
  items?: FormulaItem[];
  status?: string;
  version?: string;
  created_at?: string;
  updated_at?: string;
}

interface Assessment {
  id: number;
  formula_id: number;
  status?: string;
  notes?: string;
  created_at?: string;
  date_created?: string;
  formula?: Formula;
  formula_name?: string;
  product_name?: string;
  approval_status?: string;
  mos_status?: string;
  mos_overall?: number;
  sed_total_mg_kg_day?: number;
  report_available?: boolean;
  report_file_path?: string;
  product_category_code?: string;
}

interface TRAResult {
  id: number;
  ingredient_name: string;
  sed_mg_kg_day?: number;
  noael_mg_kg_day?: number;
  mos?: number;
  status?: string;
  flags?: string;
  notes?: string;
}

interface ProductCategory {
  id: number;
  code: string;
  default_A_mg_day?: number;
  default_F_per_day?: number;
  default_BW_kg?: number;
}

const TRAHome: React.FC = () => {
  // State management
  const [currentStep, setCurrentStep] = useState<'overview' | 'ingredients' | 'products' | 'formulas' | 'assessments'>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Ensure messages rendered in JSX are strings
  const toReadableMessage = (input: any): string => {
    if (!input) return 'Unexpected error';
    if (typeof input === 'string') return input;
    if (typeof input.message === 'string') return input.message;
    if (input.data && typeof input.data.message === 'string') return input.data.message;
    try { return JSON.stringify(input); } catch { return String(input); }
  };

  const openReviewDialog = (assessmentId: number, action: 'submit' | 'approve' | 'reject') => {
    setReviewAssessmentId(assessmentId);
    setReviewAction(action);
    setReviewComment('');
    setShowReviewModal(true);
  };

  const performReviewAction = async () => {
    if (!reviewAssessmentId || !reviewAction) return;
    setLoading(true);
    try {
      if (reviewAction === 'submit') {
        await traApi.submitReview(reviewAssessmentId, reviewComment || undefined);
        showSuccess('Submitted for review');
      } else if (reviewAction === 'approve') {
        await traApi.approveAssessment(reviewAssessmentId, reviewComment || undefined);
        showSuccess('Assessment approved');
      } else if (reviewAction === 'reject') {
        await traApi.rejectAssessment(reviewAssessmentId, reviewComment || undefined);
        showSuccess('Assessment rejected');
      }
      setShowReviewModal(false);
      setReviewAssessmentId(null);
      setReviewAction(null);
      setReviewComment('');
      await loadAllData();
    } catch (e: any) {
      showError(e?.message || 'Failed to perform review action');
    } finally {
      setLoading(false);
    }
  };

  // Data states
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  
  // Ensure arrays are always defined
  const safeIngredients = ingredients || [];
  const safeProducts = products || [];
  const safeFormulas = formulas || [];
  const safeAssessments = assessments || [];

  // Form states
  const [newIngredient, setNewIngredient] = useState({ inci_name: '', cas_number: '', function: '', noael_mg_kg_day: '', dermal_absorption_pct: '', sccs_ref: '', allergenicity: '', toxicological_class: '' });
  const [newProduct, setNewProduct] = useState({ name: '', product_type: 'cosmetic', intended_use: 'General cosmetic use', target_audience: 'Adults' });
  const [newFormula, setNewFormula] = useState({ name: '', product_id: '' });
  const [formulaItems, setFormulaItems] = useState<FormulaItem[]>([]);
  const [resultsByAssessment, setResultsByAssessment] = useState<Record<number, any[]>>({});
  const [openAssessmentResults, setOpenAssessmentResults] = useState<Record<number, boolean>>({});
  // Per-formula assessment options (category, notes)
  const [assessmentOptions, setAssessmentOptions] = useState<Record<number, { category: string; notes: string }>>({});
  // Product categories for assessments
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  // Review workflow modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState<null | 'submit' | 'approve' | 'reject'>(null);
  const [reviewAssessmentId, setReviewAssessmentId] = useState<number | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  
  // Ensure arrays are always defined (moved after formulaItems declaration)
  const safeFormulaItems = formulaItems || [];
  
  // Bulk import states
  const [bulkImportData, setBulkImportData] = useState<any[]>([]);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  
  // Formula templates
  const [showTemplates, setShowTemplates] = useState(false);
  
  // Cost calculator
  const [showCostCalculator, setShowCostCalculator] = useState(false);
  const [batchSize, setBatchSize] = useState(1000); // grams
  const [ingredientCosts, setIngredientCosts] = useState<{[key: number]: number}>({});
  
  // Comments and collaboration
  const [comments, setComments] = useState<{[key: string]: any[]}>({});
  const [newComment, setNewComment] = useState('');
  const [commentTarget, setCommentTarget] = useState<{type: string, id: number} | null>(null);
  const [showComments, setShowComments] = useState(false);
  
  // Ingredient conflicts
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);
  
  // Compliance alerts
  const [complianceAlerts, setComplianceAlerts] = useState<any[]>([]);
  const [showComplianceAlerts, setShowComplianceAlerts] = useState(false);
  
  // Formula comparison
  const [showFormulaComparison, setShowFormulaComparison] = useState(false);
  const [selectedFormulas, setSelectedFormulas] = useState<number[]>([]);
  const [comparisonResults, setComparisonResults] = useState<any>(null);
  
  // Ingredient substitution
  const [substitutionSuggestions, setSubstitutionSuggestions] = useState<any[]>([]);
  const [showSubstitutionSuggestions, setShowSubstitutionSuggestions] = useState(false);
  
  // Advanced reporting
  const [showAdvancedReporting, setShowAdvancedReporting] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [reportType, setReportType] = useState('summary');
  
  // Batch operations
  const [selectedItems, setSelectedItems] = useState<{[key: string]: number[]}>({
    ingredients: [],
    products: [],
    formulas: [],
    assessments: []
  });
  const [showBatchOperations, setShowBatchOperations] = useState(false);
  const [batchOperation, setBatchOperation] = useState<string>('');

  // Search and filters
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  
  // Formula templates data
  const formulaTemplates = {
    'anti_aging_serum': {
      name: 'Anti-Aging Serum Base',
      description: 'Basic anti-aging serum with proven ingredients',
      items: [
        { ingredient_name: 'Hyaluronic Acid', concentration_pct: 2.0 },
        { ingredient_name: 'Niacinamide', concentration_pct: 5.0 },
        { ingredient_name: 'Retinol', concentration_pct: 0.5 },
        { ingredient_name: 'Vitamin C', concentration_pct: 10.0 },
        { ingredient_name: 'Glycerin', concentration_pct: 5.0 }
      ]
    },
    'gentle_cleanser': {
      name: 'Gentle Face Cleanser',
      description: 'Mild cleanser suitable for sensitive skin',
      items: [
        { ingredient_name: 'Glycerin', concentration_pct: 3.0 },
        { ingredient_name: 'Hyaluronic Acid', concentration_pct: 1.0 },
        { ingredient_name: 'Niacinamide', concentration_pct: 2.0 }
      ]
    },
    'moisturizer_base': {
      name: 'Moisturizer Base Formula',
      description: 'Basic moisturizer with humectants and emollients',
      items: [
        { ingredient_name: 'Glycerin', concentration_pct: 8.0 },
        { ingredient_name: 'Hyaluronic Acid', concentration_pct: 2.0 },
        { ingredient_name: 'Niacinamide', concentration_pct: 4.0 }
      ]
    },
    'sunscreen_base': {
      name: 'Sunscreen Base Formula',
      description: 'Basic sunscreen with UV protection',
      items: [
        { ingredient_name: 'Zinc Oxide', concentration_pct: 15.0 },
        { ingredient_name: 'Glycerin', concentration_pct: 3.0 },
        { ingredient_name: 'Hyaluronic Acid', concentration_pct: 1.0 }
      ]
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [ingredientsRes, productsRes, formulasRes, assessmentsRes, categoriesRes] = await Promise.all([
        traApi.getIngredients(),
        traApi.getProducts(),
        traApi.getFormulas(),
        traApi.getAssessments(),
        traApi.listCategories().catch(() => ({ data: [] }))
      ]);
      
      setIngredients((ingredientsRes.data as any) || []);
      setProducts((productsRes.data as any) || []);
      setFormulas((formulasRes.data as any) || []);
      setAssessments((assessmentsRes.data as any) || []);
      setCategories((categoriesRes.data as any) || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setError(null);
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const showError = (message: string) => {
    setSuccess(null);
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  // Ingredient management
  const createIngredient = async () => {
    if (!newIngredient.inci_name.trim()) {
      showError('INCI name is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        inci_name: newIngredient.inci_name.trim(),
        cas_number: newIngredient.cas_number.trim() || null,
        function: newIngredient.function.trim() || null,
        noael_mg_kg_day: newIngredient.noael_mg_kg_day !== '' ? parseFloat(newIngredient.noael_mg_kg_day as any) : null,
        dermal_absorption_pct: newIngredient.dermal_absorption_pct !== '' ? parseFloat(newIngredient.dermal_absorption_pct as any) : null,
        sccs_ref: newIngredient.sccs_ref.trim() || null,
        allergenicity: newIngredient.allergenicity.trim() || null,
        toxicological_class: newIngredient.toxicological_class.trim() || null,
      };

      const response = await traApi.createIngredient(payload);
      
      if (response.status >= 200 && response.status < 300) {
        showSuccess('Ingredient created successfully');
        setNewIngredient({ inci_name: '', cas_number: '', function: '', noael_mg_kg_day: '', dermal_absorption_pct: '', sccs_ref: '', allergenicity: '', toxicological_class: '' });
        
        // Auto-enrich if CAS provided
        if (newIngredient.cas_number.trim()) {
          const ingredientData = response.data as any;
          if (ingredientData?.id) {
            await enrichIngredient(ingredientData.id, newIngredient.cas_number.trim());
          }
        }
        
        await loadAllData();
      } else {
        showError(response.error || 'Failed to create ingredient');
      }
    } catch (e: any) {
      showError(e?.message || 'Error creating ingredient');
    } finally {
      setLoading(false);
    }
  };

  const enrichIngredient = async (ingredientId: number, casNumber: string) => {
    try {
      const response = await traApi.enrichPubChem(ingredientId, casNumber);
      if (response.status === 200) {
        showSuccess('Ingredient enriched with PubChem data');
        await loadAllData();
      }
    } catch (e: any) {
      showError('Failed to enrich ingredient');
    }
  };

  // Bulk import functionality
  const handleBulkImportFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showError('Please select a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        const lines = csv.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const data = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          const item: any = {};
          headers.forEach((header, index) => {
            item[header] = values[index] || '';
          });
          return item;
        });
        
        setBulkImportData(data);
        setShowBulkImport(true);
        showSuccess(`Found ${data.length} ingredients to import`);
      } catch (error) {
        showError('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const processBulkImport = async () => {
    if (bulkImportData?.length === 0) return;
    
    setLoading(true);
    setImportProgress(0);
    
    try {
      for (let i = 0; i < (bulkImportData?.length || 0); i++) {
        const item = bulkImportData[i];
        const payload = {
          inci_name: item.inci_name || item.name || item.ingredient_name || '',
          cas_number: item.cas_number || item.cas || '',
          function: item.function || item.purpose || item.use || '',
        };
        
        if (payload.inci_name) {
          await traApi.createIngredient(payload);
          
          // Auto-enrich if CAS provided
          if (payload.cas_number) {
            const response = await traApi.createIngredient(payload);
            if (response.status === 200) {
              const ingredientData = response.data as any;
              if (ingredientData?.id) {
                await enrichIngredient(ingredientData.id, payload.cas_number);
              }
            }
          }
        }
        
        setImportProgress(Math.round(((i + 1) / (bulkImportData?.length || 1)) * 100));
      }
      
      showSuccess(`Successfully imported ${bulkImportData?.length || 0} ingredients`);
      setShowBulkImport(false);
      setBulkImportData([]);
      await loadAllData();
    } catch (error) {
      showError('Failed to import some ingredients');
    } finally {
      setLoading(false);
      setImportProgress(0);
    }
  };

  // Product management
  const createProduct = async () => {
    if (!newProduct.name.trim()) {
      showError('Product name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await traApi.createProduct({
        name: newProduct.name.trim(),
        product_type: newProduct.product_type,
        intended_use: newProduct.intended_use,
        target_audience: newProduct.target_audience,
        safety_data_available: true,
        ingredients: ''
      });

      if (response.status === 200) {
        showSuccess('Product created successfully');
        setNewProduct({ name: '', product_type: 'cosmetic', intended_use: 'General cosmetic use', target_audience: 'Adults' });
        await loadAllData();
      } else {
        const data: any = (response as any).data ?? (response as any).error;
        if (data && typeof data === 'object' && ('message' in data)) {
          const limitInfo = (data.max_products !== undefined) ? ` (limit ${data.max_products}, current ${data.current_products})` : '';
          showError(`${data.message}${limitInfo}`);
        } else {
          showError(toReadableMessage(data || 'Failed to create product'));
        }
      }
    } catch (e: any) {
      const serverMsg = e?.response?.data;
      if (serverMsg && typeof serverMsg === 'object' && ('message' in serverMsg)) {
        const limitInfo = (serverMsg.max_products !== undefined) ? ` (limit ${serverMsg.max_products}, current ${serverMsg.current_products})` : '';
        showError(`${serverMsg.message}${limitInfo}`);
      } else {
        showError(toReadableMessage(e?.message || e));
      }
    } finally {
      setLoading(false);
    }
  };

  // Formula management
  const createFormula = async () => {
    if (!newFormula.name.trim()) {
      showError('Formula name is required');
      return;
    }

    if (safeFormulaItems.length === 0) {
      showError('At least one ingredient is required');
      return;
    }
    setShowValidation(true);
    if (!isFormulaValid) {
      return;
    }
    setLoading(true);
    try {
      // Map formula items to include ingredient names
      const itemsWithNames = safeFormulaItems.map(item => {
        const ingredient = safeIngredients.find(ing => ing.id === item.ingredient_id);
        return {
          ingredient_id: item.ingredient_id > 0 ? item.ingredient_id : null,
          ingredient_name: ingredient?.inci_name || item.temp_name || 'Unknown Ingredient',
          concentration_pct: item.concentration_pct,
          override_da_pct: item.override_da_pct ?? null,
          override_noael_mg_kg_day: item.override_noael_mg_kg_day ?? null
        } as any;
      });

      const response = await traApi.createFormula({
        name: newFormula.name.trim(),
        product_id: newFormula.product_id ? parseInt(newFormula.product_id) : null,
        items: itemsWithNames
      });

      if (response.status >= 200 && response.status < 300) {
        showSuccess('Formula created successfully');
        setNewFormula({ name: '', product_id: '' });
        setFormulaItems([]);
        setShowValidation(false);
        await loadAllData();
      } else {
        const data: any = (response as any).data ?? (response as any).error;
        showError(toReadableMessage(data || 'Failed to create formula'));
      }
    } catch (e: any) {
      const serverMsg = e?.response?.data ?? e?.message ?? e;
      showError(toReadableMessage(serverMsg));
    } finally {
      setLoading(false);
    }
  };

  const addFormulaItem = () => {
    setFormulaItems([...formulaItems, { ingredient_id: 0, concentration_pct: 0, override_da_pct: null, override_noael_mg_kg_day: null }]);
  };

  const updateFormulaItem = (index: number, field: keyof FormulaItem, value: number | null | undefined) => {
    const updated = [...formulaItems];
    (updated[index] as any)[field] = value;
    setFormulaItems(updated);
  };

  const updateFormulaItemName = (index: number, value: string) => {
    const updated = [...formulaItems];
    updated[index].temp_name = value;
    setFormulaItems(updated);
  };

  const toggleAssessmentResults = async (assessmentId: number) => {
    try {
      const isOpen = !!openAssessmentResults[assessmentId];
      if (!isOpen && !resultsByAssessment[assessmentId]) {
        const res = await traApi.listResults(assessmentId);
        setResultsByAssessment({ ...resultsByAssessment, [assessmentId]: (res.data as any) || [] });
      }
      setOpenAssessmentResults({ ...openAssessmentResults, [assessmentId]: !isOpen });
    } catch (e: any) {
      showError(e?.message || 'Failed to load results');
    }
  };

  const removeFormulaItem = (index: number) => {
    setFormulaItems(safeFormulaItems.filter((_, i) => i !== index));
  };

  const applyTemplate = (templateKey: string) => {
    const template = formulaTemplates[templateKey as keyof typeof formulaTemplates];
    if (!template) return;
    
    // Map template items to current ingredients
    const mappedItems = template.items.map(templateItem => {
      const ingredient = ingredients.find(ing => 
        ing.inci_name.toLowerCase() === templateItem.ingredient_name.toLowerCase()
      );
      return {
        ingredient_id: ingredient?.id || 0,
        concentration_pct: templateItem.concentration_pct
      };
    }).filter(item => item.ingredient_id > 0);
    
    setFormulaItems(mappedItems);
    setNewFormula({ ...newFormula, name: template.name });
    setShowTemplates(false);
    showSuccess(`Applied template: ${template.name}`);
  };

  // Cost calculation
  const calculateFormulaCost = () => {
    let totalCost = 0;
    const costBreakdown = safeFormulaItems.map(item => {
      const ingredient = safeIngredients.find(ing => ing.id === item.ingredient_id);
      const costPerGram = ingredientCosts[item.ingredient_id] || 0;
      const amountInGrams = (item.concentration_pct / 100) * batchSize;
      const itemCost = amountInGrams * costPerGram;
      totalCost += itemCost;
      
      return {
        ingredient: ingredient?.inci_name || 'Unknown',
        concentration: item.concentration_pct,
        amount: amountInGrams,
        costPerGram,
        totalCost: itemCost
      };
    });
    
    return { totalCost, costBreakdown };
  };

  const { totalCost, costBreakdown } = calculateFormulaCost();

  // Formula validation
  const validateFormula = () => {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Check if formula has a name
    if (!newFormula.name.trim()) {
      errors.push('Formula name is required');
    }
    
    // Check if formula has ingredients
    if (safeFormulaItems.length === 0) {
      errors.push('Formula must have at least one ingredient');
    }
    
    // Check concentration totals
    const totalConcentration = safeFormulaItems.reduce((sum, item) => sum + (item.concentration_pct || 0), 0);
    if (totalConcentration > 100) {
      errors.push(`Total concentration is ${totalConcentration.toFixed(1)}%, which exceeds 100%`);
    } else if (totalConcentration < 95) {
      warnings.push(`Total concentration is ${totalConcentration.toFixed(1)}%, consider if this is intentional`);
    }
    
    // Check for duplicate ingredients
    const ingredientIds = safeFormulaItems.map(item => item.ingredient_id).filter(id => id > 0);
    const uniqueIds = new Set(ingredientIds);
    if (ingredientIds.length !== uniqueIds.size) {
      errors.push('Formula contains duplicate ingredients');
    }
    
    // Check for zero concentrations
    const zeroConcentrations = safeFormulaItems.filter(item => item.concentration_pct === 0);
    if (zeroConcentrations.length > 0) {
      warnings.push(`${zeroConcentrations.length} ingredient(s) have 0% concentration`);
    }
    
    // Check for very low concentrations
    const lowConcentrations = safeFormulaItems.filter(item => item.concentration_pct > 0 && item.concentration_pct < 0.1);
    if (lowConcentrations.length > 0) {
      warnings.push(`${lowConcentrations.length} ingredient(s) have very low concentrations (<0.1%)`);
    }
    
    return { errors, warnings, isValid: errors.length === 0 };
  };

  const [showValidation, setShowValidation] = useState(false);
  const { errors: validationErrors, warnings: validationWarnings, isValid: isFormulaValid } = validateFormula();

  // Comments and collaboration functionality
  const addComment = (type: string, id: number, comment: string) => {
    if (!comment.trim()) return;
    
    const commentKey = `${type}_${id}`;
    const newCommentObj = {
      id: Date.now(),
      text: comment,
      author: 'Current User',
      timestamp: new Date().toISOString(),
      type: type
    };
    
    setComments(prev => ({
      ...prev,
      [commentKey]: [...(prev[commentKey] || []), newCommentObj]
    }));
    
    setNewComment('');
    showSuccess('Comment added successfully');
  };

  const getComments = (type: string, id: number) => {
    const commentKey = `${type}_${id}`;
    return comments[commentKey] || [];
  };

  const openComments = (type: string, id: number) => {
    setCommentTarget({ type, id });
    setShowComments(true);
  };

  // Ingredient conflict detection
  const detectConflicts = () => {
    const detectedConflicts: any[] = [];
    
    // Known ingredient conflicts database
    const conflictRules = {
      'Retinol': {
        conflicts: ['Vitamin C', 'Benzoyl Peroxide', 'Alpha Hydroxy Acids'],
        reason: 'pH incompatibility and potential irritation',
        severity: 'high'
      },
      'Vitamin C': {
        conflicts: ['Retinol', 'Niacinamide'],
        reason: 'pH incompatibility and stability issues',
        severity: 'medium'
      },
      'Benzoyl Peroxide': {
        conflicts: ['Retinol', 'Alpha Hydroxy Acids'],
        reason: 'Can cause excessive dryness and irritation',
        severity: 'high'
      },
      'Alpha Hydroxy Acids': {
        conflicts: ['Retinol', 'Benzoyl Peroxide', 'Beta Hydroxy Acids'],
        reason: 'Can cause over-exfoliation and irritation',
        severity: 'medium'
      },
      'Beta Hydroxy Acids': {
        conflicts: ['Alpha Hydroxy Acids'],
        reason: 'Over-exfoliation risk',
        severity: 'low'
      }
    };
    
    // Check for conflicts in current formula
    const currentIngredients = formulaItems
      .map(item => safeIngredients.find(ing => ing.id === item.ingredient_id))
      .filter(Boolean)
      .map(ing => ing!.inci_name);
    
    currentIngredients.forEach(ingredient => {
      const rule = conflictRules[ingredient as keyof typeof conflictRules];
      if (rule) {
        const conflictingIngredients = currentIngredients.filter(ing => 
          rule.conflicts.includes(ing) && ing !== ingredient
        );
        
        if (conflictingIngredients.length > 0) {
          detectedConflicts.push({
            ingredient,
            conflictingWith: conflictingIngredients,
            reason: rule.reason,
            severity: rule.severity,
            recommendation: `Consider removing ${conflictingIngredients.join(' or ')} or use at different times`
          });
        }
      }
    });
    
    // Check for concentration conflicts
    const highConcentrationIngredients = safeFormulaItems.filter(item => item.concentration_pct > 10);
    if (highConcentrationIngredients.length > 2) {
      detectedConflicts.push({
        type: 'concentration',
        reason: 'Multiple ingredients at high concentrations (>10%) may cause irritation',
        severity: 'medium',
        recommendation: 'Consider reducing concentrations or adding soothing ingredients'
      });
    }
    
    setConflicts(detectedConflicts);
    setShowConflicts(detectedConflicts.length > 0);
    
    if (detectedConflicts.length > 0) {
      showError(`${detectedConflicts.length} potential conflicts detected`);
    }
  };

  // Compliance alerts system
  const checkComplianceAlerts = () => {
    const alerts: any[] = [];
    
    // Check for regulatory compliance issues
    const currentIngredients = formulaItems
      .map(item => safeIngredients.find(ing => ing.id === item.ingredient_id))
      .filter(Boolean)
      .map(ing => ing!);
    
    // Check for banned/restricted ingredients
    const bannedIngredients = ['Mercury', 'Lead', 'Arsenic', 'Cadmium'];
    const restrictedIngredients = ['Formaldehyde', 'Parabens', 'Sulfates'];
    
    currentIngredients.forEach(ingredient => {
      const inciName = ingredient.inci_name.toLowerCase();
      
      // Check for banned ingredients
      bannedIngredients.forEach(banned => {
        if (inciName.includes(banned.toLowerCase())) {
          alerts.push({
            type: 'banned',
            severity: 'critical',
            ingredient: ingredient.inci_name,
            message: `${ingredient.inci_name} is banned in cosmetic products`,
            regulation: 'EU Cosmetics Regulation 1223/2009',
            action: 'Remove ingredient immediately'
          });
        }
      });
      
      // Check for restricted ingredients
      restrictedIngredients.forEach(restricted => {
        if (inciName.includes(restricted.toLowerCase())) {
          alerts.push({
            type: 'restricted',
            severity: 'warning',
            ingredient: ingredient.inci_name,
            message: `${ingredient.inci_name} has usage restrictions`,
            regulation: 'EU Cosmetics Regulation 1223/2009',
            action: 'Check concentration limits and usage conditions'
          });
        }
      });
    });
    
    // Check for concentration compliance
    safeFormulaItems.forEach(item => {
      const ingredient = safeIngredients.find(ing => ing.id === item.ingredient_id);
      if (ingredient) {
        // Check for high concentration warnings
        if (item.concentration_pct > 20) {
          alerts.push({
            type: 'concentration',
            severity: 'warning',
            ingredient: ingredient.inci_name,
            message: `High concentration (${item.concentration_pct}%) may require special labeling`,
            regulation: 'EU Cosmetics Regulation 1223/2009',
            action: 'Review concentration and add appropriate warnings'
          });
        }
        
        // Check for specific ingredient limits
        if (ingredient.inci_name.toLowerCase().includes('retinol') && item.concentration_pct > 0.3) {
          alerts.push({
            type: 'limit_exceeded',
            severity: 'error',
            ingredient: ingredient.inci_name,
            message: `Retinol concentration (${item.concentration_pct}%) exceeds recommended limit (0.3%)`,
            regulation: 'SCCS Opinion on Retinol',
            action: 'Reduce concentration to 0.3% or below'
          });
        }
      }
    });
    
    // Check for missing safety data
    const ingredientsWithoutData = currentIngredients.filter(ing => !ing.metadata_json);
    if (ingredientsWithoutData.length > 0) {
      alerts.push({
        type: 'missing_data',
        severity: 'info',
        message: `${ingredientsWithoutData.length} ingredients lack safety data`,
        action: 'Consider enriching with PubChem data or provide safety documentation'
      });
    }
    
    setComplianceAlerts(alerts);
    setShowComplianceAlerts(alerts.length > 0);
    
    if (alerts.length > 0) {
      const criticalCount = alerts.filter(a => a.severity === 'critical').length;
      const warningCount = alerts.filter(a => a.severity === 'warning').length;
      
      if (criticalCount > 0) {
        showError(`🚨 ${criticalCount} critical compliance issues detected!`);
      } else if (warningCount > 0) {
        showError(`⚠️ ${warningCount} compliance warnings detected`);
      } else {
        showSuccess(`ℹ️ ${alerts.length} compliance notices`);
      }
    } else {
      showSuccess('✅ No compliance issues detected');
    }
  };

  // Formula comparison functionality
  const compareFormulas = () => {
    if ((selectedFormulas?.length || 0) < 2) {
      showError('Please select at least 2 formulas to compare');
      return;
    }
    
    const formulasToCompare = safeFormulas.filter(f => selectedFormulas.includes(f.id));
    const comparison = {
      formulas: formulasToCompare,
      commonIngredients: [],
      uniqueIngredients: {},
      concentrationDifferences: {},
      totalConcentration: {},
      costComparison: {},
      safetyComparison: {}
    };
    
    // Find common ingredients
    formulasToCompare.forEach(formula => {
      // This would need to be expanded to get actual formula items
      // For now, we'll create a mock comparison
    });
    
    // Calculate differences
    formulasToCompare.forEach((formula, index) => {
      (comparison.totalConcentration as any)[formula.id] = Math.random() * 100; // Mock data
      (comparison.costComparison as any)[formula.id] = Math.random() * 50; // Mock data
    });
    
    setComparisonResults(comparison);
    setShowFormulaComparison(true);
    showSuccess(`Comparing ${formulasToCompare.length} formulas`);
  };

  const toggleFormulaSelection = (formulaId: number) => {
    setSelectedFormulas(prev => 
      prev.includes(formulaId) 
        ? prev.filter(id => id !== formulaId)
        : [...prev, formulaId]
    );
  };

  // Ingredient substitution suggestions
  const getSubstitutionSuggestions = (ingredientId: number) => {
    const ingredient = safeIngredients.find(ing => ing.id === ingredientId);
    if (!ingredient) return;
    
    const suggestions: any[] = [];
    const inciName = ingredient.inci_name.toLowerCase();
    
    // Define substitution rules based on ingredient function and type
    const substitutionRules: {[key: string]: any[]} = {
      'emollient': [
        { name: 'Caprylic/Capric Triglyceride', reason: 'Similar emollient properties, better stability', compatibility: 95 },
        { name: 'Squalane', reason: 'Natural emollient, excellent skin compatibility', compatibility: 90 },
        { name: 'Dimethicone', reason: 'Silicone-based emollient, superior spreadability', compatibility: 85 }
      ],
      'humectant': [
        { name: 'Hyaluronic Acid', reason: 'Superior water retention, anti-aging benefits', compatibility: 95 },
        { name: 'Glycerin', reason: 'Classic humectant, cost-effective', compatibility: 90 },
        { name: 'Sodium PCA', reason: 'Natural humectant, skin-identical', compatibility: 85 }
      ],
      'preservative': [
        { name: 'Phenoxyethanol', reason: 'Broad spectrum, widely accepted', compatibility: 90 },
        { name: 'Benzyl Alcohol', reason: 'Natural preservative, gentle', compatibility: 85 },
        { name: 'Potassium Sorbate', reason: 'Food-grade preservative, natural', compatibility: 80 }
      ],
      'surfactant': [
        { name: 'Sodium Lauryl Sulfoacetate', reason: 'Gentle surfactant, sulfate-free', compatibility: 90 },
        { name: 'Cocamidopropyl Betaine', reason: 'Mild amphoteric surfactant', compatibility: 85 },
        { name: 'Decyl Glucoside', reason: 'Natural surfactant, biodegradable', compatibility: 80 }
      ],
      'antioxidant': [
        { name: 'Vitamin E (Tocopherol)', reason: 'Natural antioxidant, skin benefits', compatibility: 95 },
        { name: 'Ascorbic Acid', reason: 'Vitamin C, brightening properties', compatibility: 90 },
        { name: 'Ferulic Acid', reason: 'Stabilizes other antioxidants', compatibility: 85 }
      ]
    };
    
    // Find matching function
    const function_ = ingredient.function?.toLowerCase() || '';
    const matchingRules = Object.keys(substitutionRules).find(key => 
      function_.includes(key) || inciName.includes(key)
    );
    
    if (matchingRules) {
      suggestions.push(...substitutionRules[matchingRules].map(suggestion => ({
        ...suggestion,
        originalIngredient: ingredient.inci_name,
        originalFunction: ingredient.function,
        costImpact: Math.random() > 0.5 ? 'Lower cost' : 'Higher cost',
        availability: Math.random() > 0.3 ? 'Readily available' : 'Special order'
      })));
    }
    
    // Add general suggestions based on ingredient type
    if (inciName.includes('paraben')) {
      suggestions.push({
        name: 'Phenoxyethanol + Caprylyl Glycol',
        reason: 'Paraben-free preservative system',
        compatibility: 85,
        originalIngredient: ingredient.inci_name,
        originalFunction: ingredient.function,
        costImpact: 'Similar cost',
        availability: 'Readily available'
      });
    }
    
    if (inciName.includes('sulfate')) {
      suggestions.push({
        name: 'Sodium Lauryl Sulfoacetate',
        reason: 'Sulfate-free alternative with similar cleansing',
        compatibility: 80,
        originalIngredient: ingredient.inci_name,
        originalFunction: ingredient.function,
        costImpact: 'Slightly higher cost',
        availability: 'Readily available'
      });
    }
    
    setSubstitutionSuggestions(suggestions);
    setShowSubstitutionSuggestions(suggestions.length > 0);
    
    if (suggestions.length > 0) {
      showSuccess(`Found ${suggestions.length} substitution suggestions for ${ingredient.inci_name}`);
    } else {
      showError('No substitution suggestions found for this ingredient');
    }
  };

  // Advanced reporting functionality
  const generateAdvancedReport = (type: string) => {
    const report: any = {
      generatedAt: new Date().toISOString(),
      type: type,
      summary: {},
      details: {},
      charts: {},
      recommendations: []
    };

    switch (type) {
      case 'summary':
        report.summary = {
          totalIngredients: safeIngredients.length,
          totalProducts: safeProducts.length,
          totalFormulas: safeFormulas.length,
          totalAssessments: safeAssessments.length,
          enrichedIngredients: safeIngredients.filter(ing => ing.metadata_json).length,
          complianceIssues: complianceAlerts.length,
          conflicts: conflicts.length
        };
        break;

      case 'ingredients':
        report.details = {
          byFunction: safeIngredients.reduce((acc: any, ing) => {
            const func = ing.function || 'Unknown';
            acc[func] = (acc[func] || 0) + 1;
            return acc;
          }, {}),
          enrichedPercentage: safeIngredients.length > 0 ? (safeIngredients.filter(ing => ing.metadata_json).length / safeIngredients.length * 100).toFixed(1) : '0',
          topIngredients: safeIngredients
            .sort((a, b) => (b.metadata_json ? 1 : 0) - (a.metadata_json ? 1 : 0))
            .slice(0, 10)
            .map(ing => ({
              name: ing.inci_name,
              function: ing.function,
              enriched: !!ing.metadata_json
            }))
        };
        break;

      case 'formulas': {
        const totalItems = safeFormulas.reduce((sum, f) => sum + (f.items?.length || 0), 0);
        const avg = safeFormulas.length > 0 ? (totalItems / safeFormulas.length) : 0;
        const totals = safeFormulas.map(f => (f.items?.reduce((s, it) => s + (it.concentration_pct || 0), 0) || 0));
        const min = totals.length ? Math.min(...totals) : 0;
        const max = totals.length ? Math.max(...totals) : 0;
        const median = totals.length ? [...totals].sort((a,b)=>a-b)[Math.floor(totals.length/2)] : 0;
        report.details = {
          averageIngredients: Number(avg.toFixed(2)),
          totalConcentration: { min, median, max },
          distribution: {
            zeroItemFormulas: safeFormulas.filter(f => (f.items?.length || 0) === 0).length,
            oneToThreeItems: safeFormulas.filter(f => {
              const n = f.items?.length || 0; return n >= 1 && n <= 3;
            }).length,
            fourToTenItems: safeFormulas.filter(f => {
              const n = f.items?.length || 0; return n >= 4 && n <= 10;
            }).length,
            moreThanTen: safeFormulas.filter(f => (f.items?.length || 0) > 10).length,
          }
        };
        break;
      }

      case 'compliance':
        report.details = {
          criticalIssues: complianceAlerts.filter(a => a.severity === 'critical').length,
          warnings: complianceAlerts.filter(a => a.severity === 'warning').length,
          info: complianceAlerts.filter(a => a.severity === 'info').length,
          byType: complianceAlerts.reduce((acc, alert) => {
            acc[alert.type] = (acc[alert.type] || 0) + 1;
            return acc;
          }, {}),
          recommendations: [
            'Review all critical compliance issues immediately',
            'Update ingredient database with latest regulatory information',
            'Implement automated compliance checking in formula creation',
            'Schedule regular compliance audits'
          ]
        };
        break;

      case 'cost':
        report.details = {
          estimatedCosts: safeFormulas.map(f => ({
            name: f.name,
            estimatedCost: Math.random() * 50 + 10, // Mock data
            costPerUnit: Math.random() * 0.5 + 0.1
          })),
          costOptimization: [
            'Consider bulk purchasing for frequently used ingredients',
            'Evaluate alternative suppliers for high-cost ingredients',
            'Review concentration levels for cost optimization opportunities'
          ]
        };
        break;
    }

    setReportData(report);
    setShowAdvancedReporting(true);
    showSuccess(`Generated ${type} report successfully`);
  };

  // Batch operations functionality
  const toggleItemSelection = (type: string, id: number) => {
    setSelectedItems(prev => ({
      ...prev,
      [type]: prev[type].includes(id) 
        ? prev[type].filter(itemId => itemId !== id)
        : [...prev[type], id]
    }));
  };

  const selectAllItems = (type: string, items: any[]) => {
    const allIds = items.map(item => item.id);
    setSelectedItems(prev => ({
      ...prev,
      [type]: allIds
    }));
  };

  const clearSelection = (type: string) => {
    setSelectedItems(prev => ({
      ...prev,
      [type]: []
    }));
  };

  const executeBatchOperation = async () => {
    if (!batchOperation) return;
    
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      for (const [type, ids] of Object.entries(selectedItems)) {
        if (ids.length === 0) continue;
        
        for (const id of ids) {
          try {
            switch (batchOperation) {
              case 'export':
                // Export selected items
                exportToExcel(type);
                break;
              case 'enrich':
                if (type === 'ingredients') {
                  const ingredient = ingredients.find(ing => ing.id === id);
                  if (ingredient?.cas_number) {
                    await enrichIngredient(id, ingredient.cas_number);
                  }
                }
                break;
            }
            successCount++;
          } catch (error) {
            errorCount++;
          }
        }
      }
      
      if (successCount > 0) {
        showSuccess(`Batch operation completed: ${successCount} items processed`);
      }
      if (errorCount > 0) {
        showError(`${errorCount} items failed to process`);
      }
      
      // Clear selections and refresh data
      setSelectedItems({
        ingredients: [],
        products: [],
        formulas: [],
        assessments: []
      });
      setShowBatchOperations(false);
      await loadAllData();
      
    } catch (error) {
      showError('Batch operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Global search functionality
  const performGlobalSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    const results: any[] = [];
    const searchTerm = query.toLowerCase();
    
    // Search ingredients
    ingredients.forEach(ingredient => {
      if (ingredient.inci_name.toLowerCase().includes(searchTerm) ||
          ingredient.cas_number?.toLowerCase().includes(searchTerm) ||
          ingredient.function?.toLowerCase().includes(searchTerm)) {
        results.push({
          type: 'ingredient',
          id: ingredient.id,
          name: ingredient.inci_name,
          description: `CAS: ${ingredient.cas_number || 'N/A'} | Function: ${ingredient.function || 'N/A'}`,
          data: ingredient
        });
      }
    });
    
    // Search products
    products.forEach(product => {
      if (product.name.toLowerCase().includes(searchTerm) ||
          product.product_type?.toLowerCase().includes(searchTerm) ||
          product.intended_use?.toLowerCase().includes(searchTerm)) {
        results.push({
          type: 'product',
          id: product.id,
          name: product.name,
          description: `Type: ${product.product_type || 'N/A'} | Use: ${product.intended_use || 'N/A'}`,
          data: product
        });
      }
    });
    
    // Search formulas
    formulas.forEach(formula => {
      if (formula.name.toLowerCase().includes(searchTerm)) {
        results.push({
          type: 'formula',
          id: formula.id,
          name: formula.name,
          description: `Product ID: ${formula.product_id} | Items: ${formula.items?.length || 0}`,
          data: formula
        });
      }
    });
    
    // Search assessments
    assessments.forEach(assessment => {
      if (assessment.status?.toLowerCase().includes(searchTerm) ||
          assessment.notes?.toLowerCase().includes(searchTerm)) {
        results.push({
          type: 'assessment',
          id: assessment.id,
          name: `Assessment #${assessment.id}`,
          description: `Status: ${assessment.status || 'N/A'} | Formula ID: ${assessment.formula_id}`,
          data: assessment
        });
      }
    });
    
    setSearchResults(results);
    setShowSearchResults(true);
  };

  const handleGlobalSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setGlobalSearch(query);
    performGlobalSearch(query);
  };

  const clearGlobalSearch = () => {
    setGlobalSearch('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Excel export functionality
  const exportToExcel = (dataType: string) => {
    let data: any[] = [];
    let filename = '';
    
    switch (dataType) {
      case 'ingredients':
        data = safeIngredients.map(ing => ({
          'INCI Name': ing.inci_name,
          'CAS Number': ing.cas_number || '',
          'Function': ing.function || '',
          'NOAEL (mg/kg/day)': ing.noael_mg_kg_day ? ing.noael_mg_kg_day.toString() : '',
          'Dermal Absorption %': ing.dermal_absorption_pct ? ing.dermal_absorption_pct.toString() : '',
          'SCCS Reference': ing.sccs_ref || '',
          'Allergenicity': ing.allergenicity || '',
          'Toxicological Class': ing.toxicological_class || '',
          'Created': ing.created_at ? new Date(ing.created_at).toLocaleDateString() : ''
        }));
        filename = 'ingredients_export.xlsx';
        break;
        
      case 'products':
        data = safeProducts.map(prod => ({
          'Product Name': prod.name,
          'Type': prod.product_type || '',
          'Intended Use': prod.intended_use || '',
          'Target Audience': prod.target_audience || '',
          'Safety Data Available': prod.safety_data_available ? 'Yes' : 'No',
          'Created': prod.created_at ? new Date(prod.created_at).toLocaleDateString() : ''
        }));
        filename = 'products_export.xlsx';
        break;
        
      case 'formulas':
        data = safeFormulas.map(formula => ({
          'Formula Name': formula.name,
          'Product ID': formula.product_id,
          'Items Count': formula.items?.length || 0,
          'Total Concentration': formula.items?.reduce((sum, item) => sum + (item.concentration_pct || 0), 0).toFixed(1) + '%' || '0%',
          'Created': formula.created_at ? new Date(formula.created_at).toLocaleDateString() : ''
        }));
        filename = 'formulas_export.xlsx';
        break;
        
      case 'assessments':
        data = safeAssessments.map(assess => ({
          'Assessment ID': assess.id,
          'Formula ID': assess.formula_id,
          'Status': assess.status || '',
          'Product Category': assess.product_category_code || '',
          'Notes': assess.notes || '',
          'Created': assess.created_at ? new Date(assess.created_at).toLocaleDateString() : ''
        }));
        filename = 'assessments_export.xlsx';
        break;
        
      case 'all':
        // Create a workbook with multiple sheets
        const workbook = XLSX.utils.book_new();
        
        // Ingredients sheet
        const ingredientsData = safeIngredients.map(ing => ({
          'INCI Name': ing.inci_name,
          'CAS Number': ing.cas_number || '',
          'Function': ing.function || '',
          'NOAEL (mg/kg/day)': ing.noael_mg_kg_day ? ing.noael_mg_kg_day.toString() : '',
          'Dermal Absorption %': ing.dermal_absorption_pct ? ing.dermal_absorption_pct.toString() : '',
          'SCCS Reference': ing.sccs_ref || ''
        }));
        const ingredientsSheet = XLSX.utils.json_to_sheet(ingredientsData);
        XLSX.utils.book_append_sheet(workbook, ingredientsSheet, 'Ingredients');
        
        // Products sheet
        const productsData = safeProducts.map(prod => ({
          'Product Name': prod.name,
          'Type': prod.product_type || '',
          'Intended Use': prod.intended_use || '',
          'Target Audience': prod.target_audience || ''
        }));
        const productsSheet = XLSX.utils.json_to_sheet(productsData);
        XLSX.utils.book_append_sheet(workbook, productsSheet, 'Products');
        
        // Formulas sheet
        const formulasData = safeFormulas.map(formula => ({
          'Formula Name': formula.name,
          'Product ID': formula.product_id,
          'Items Count': formula.items?.length || 0
        }));
        const formulasSheet = XLSX.utils.json_to_sheet(formulasData);
        XLSX.utils.book_append_sheet(workbook, formulasSheet, 'Formulas');
        
        // Assessments sheet
        const assessmentsData = safeAssessments.map(assess => ({
          'Assessment ID': assess.id,
          'Formula ID': assess.formula_id,
          'Status': assess.status || '',
          'Product Category': assess.product_category_code || ''
        }));
        const assessmentsSheet = XLSX.utils.json_to_sheet(assessmentsData);
        XLSX.utils.book_append_sheet(workbook, assessmentsSheet, 'Assessments');
        
        XLSX.writeFile(workbook, 'tra_module_export.xlsx');
        showSuccess('Complete data exported to Excel');
        return;
    }
    
    if (data.length === 0) {
      showError(`No ${dataType} data to export`);
      return;
    }
    
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, dataType.charAt(0).toUpperCase() + dataType.slice(1));
    XLSX.writeFile(workbook, filename);
    showSuccess(`${dataType.charAt(0).toUpperCase() + dataType.slice(1)} exported to Excel`);
  };

  // Assessment management
  const createAssessment = async (formulaId: number) => {
    setLoading(true);
    try {
      const opts = assessmentOptions[formulaId] || { category: 'LEAVE_ON_FACE', notes: 'New assessment' };
      const response = await traApi.createAssessment(formulaId, opts.category || 'LEAVE_ON_FACE', opts.notes || 'New assessment');

      if (response.status >= 200 && response.status < 300) {
        showSuccess('Assessment created successfully');
        await loadAllData();
      } else {
        showError(response.error || 'Failed to create assessment');
      }
    } catch (e: any) {
      showError(e?.message || 'Error creating assessment');
    } finally {
      setLoading(false);
    }
  };

  const calculateAssessment = async (assessmentId: number) => {
    setLoading(true);
    try {
      const response = await traApi.calculateAssessment(assessmentId);
      
      if (response.status >= 200 && response.status < 300) {
        showSuccess('Assessment calculated successfully');
        await loadAllData();
      } else {
        showError(response.error || 'Failed to calculate assessment');
      }
    } catch (e: any) {
      showError(e?.message || 'Error calculating assessment');
    } finally {
      setLoading(false);
    }
  };

  // Filtered data
  const filteredIngredients = safeIngredients.filter(ing => 
    ing.inci_name.toLowerCase().includes(ingredientSearch.toLowerCase()) ||
    (ing.cas_number && ing.cas_number.includes(ingredientSearch))
  );

  const filteredProducts = safeProducts.filter(prod =>
    prod.name.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Step descriptions for the dynamic banner
  const stepInfo: Record<string, { title: string; desc: string; bullets: string[] }> = {
    overview: {
      title: 'TRA Module Overview',
      desc: 'End‑to‑end Toxicological Risk Assessment for cosmetic products: curate ingredients, build formulas, calculate exposure (SED) and Margin of Safety (MoS), route for review, and generate reports.',
      bullets: ['Ingredient repository with NOAEL & Dermal Absorption', 'Formula creation with validation (≤ 100%)', 'Per‑ingredient SED and overall MoS', 'Toxicologist review workflow and PDF report', 'PFAS compliance (CA/FR), inline PFAS badge, PDF section']
    },
    ingredients: {
      title: 'Ingredients',
      desc: 'Create and manage ingredients with INCI, CAS, function, and toxicology attributes. Optionally enrich from PubChem.',
      bullets: ['INCI/CAS/function metadata', 'NOAEL (mg/kg/day), Dermal Absorption %', 'SCCS reference & allergen/tox class', 'CSV bulk import + enrichment']
    },
    products: {
      title: 'Products',
      desc: 'Register product shells to associate formulas with intended use and audience.',
      bullets: ['Type & intended use', 'Target audience', 'Safety data availability']
    },
    formulas: {
      title: 'Formulas',
      desc: 'Assemble ingredients into formulas, validate totals, and set per‑item overrides for DA%/NOAEL when needed.',
      bullets: ['Total concentration validation', 'Template application', 'Cost estimation', 'Conflict & compliance checks', 'PFAS Check (CA/FR) button and inline PFAS badge']
    },
    assessments: {
      title: 'Assessments',
      desc: 'Calculate SED and MoS using product category defaults (A, F, BW). Submit for review, approve/reject, and export PDF.',
      bullets: ['Per‑ingredient SED & MoS status', 'Overall MoS', 'Review workflow', 'PDF/XLSX report generation', 'PFAS Compliance in notes and PDF']
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Toxicological Risk Assessment</h1>
          <p className="text-gray-800">Manage ingredients, products, formulas, and safety assessments</p>
        </div>

        {/* Dynamic Top Banner */}
        <div className="mb-6">
          <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <div className="text-sm uppercase tracking-wide text-blue-700 font-semibold mb-1">{stepInfo[currentStep]?.title}</div>
                <div className="text-gray-900 text-lg font-medium mb-2">{stepInfo[currentStep]?.desc}</div>
                <div className="flex flex-wrap gap-2">
                  {stepInfo[currentStep]?.bullets.map((b, i) => (
                    <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-blue-700 border border-blue-200">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
              {/* Simple inline flow diagram */}
              <div className="w-full lg:w-auto">
                <svg width="360" height="110" viewBox="0 0 360 110" className="mx-auto">
                  {/* Boxes */}
                  <g>
                    <rect x="5" y="20" rx="10" ry="10" width="90" height="40" className={`${currentStep==='ingredients'?'fill-blue-600 text-white':'fill-white'} stroke-blue-300`} strokeWidth="1" />
                    <text x="50" y="45" textAnchor="middle" className={`${currentStep==='ingredients'?'fill-white':'fill-blue-800'} text-[12px] font-semibold`}>Ingredients</text>

                    <rect x="120" y="20" rx="10" ry="10" width="90" height="40" className={`${currentStep==='formulas'?'fill-blue-600':'fill-white'} stroke-blue-300`} strokeWidth="1" />
                    <text x="165" y="45" textAnchor="middle" className={`${currentStep==='formulas'?'fill-white':'fill-blue-800'} text-[12px] font-semibold`}>Formulas</text>

                    <rect x="235" y="20" rx="10" ry="10" width="110" height="40" className={`${currentStep==='assessments'?'fill-blue-600':'fill-white'} stroke-blue-300`} strokeWidth="1" />
                    <text x="290" y="45" textAnchor="middle" className={`${currentStep==='assessments'?'fill-white':'fill-blue-800'} text-[12px] font-semibold`}>Assessments</text>
                  </g>
                  {/* Arrows */}
                  <g stroke="#60a5fa" strokeWidth="2" fill="none">
                    <line x1="95" y1="40" x2="120" y2="40" />
                    <polygon points="120,40 114,36 114,44" fill="#60a5fa" />
                    <line x1="210" y1="40" x2="235" y2="40" />
                    <polygon points="235,40 229,36 229,44" fill="#60a5fa" />
                  </g>
                  {/* Bottom: Report */}
                  <g>
                    <rect x="140" y="70" rx="8" ry="8" width="80" height="30" className="fill-white stroke-green-300" strokeWidth="1" />
                    <text x="180" y="88" textAnchor="middle" className="fill-green-800 text-[12px] font-semibold">PDF Report</text>
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Global Search */}
        <div className="mb-6 relative">
          <div className="relative">
            <input
              type="text"
              value={globalSearch}
              onChange={handleGlobalSearch}
              placeholder="Search ingredients, products, formulas, assessments..."
              className="w-full px-4 py-3 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
                </div>
            {globalSearch && (
              <button
                onClick={clearGlobalSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
                </div>
          
          {showSearchResults && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
              {searchResults?.length > 0 ? (
                <div className="py-2">
                  {searchResults?.map((result: any, index: number) => (
                    <div key={index} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                      <div className="flex items-center justify-between">
                <div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              result.type === 'ingredient' ? 'bg-blue-100 text-blue-800' :
                              result.type === 'product' ? 'bg-green-100 text-green-800' :
                              result.type === 'formula' ? 'bg-purple-100 text-purple-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {result.type}
                            </span>
                            <span className="font-medium text-gray-900">{result.name}</span>
                </div>
                          <p className="text-sm text-gray-700 mt-1">{result.description}</p>
                </div>
                </div>
                </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-3 text-gray-500 text-center">
                  No results found for "{globalSearch}"
              </div>
            )}
          </div>
          )}
        </div>

        {/* Steps + Export */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {[
                { key: 'overview', label: 'Overview', icon: '🧭' },
                { key: 'ingredients', label: 'Ingredients', icon: '🧪' },
                { key: 'products', label: 'Products', icon: '📦' },
                { key: 'formulas', label: 'Formulas', icon: '⚗️' },
                { key: 'assessments', label: 'Assessments', icon: '📊' }
              ].map((step, index) => (
                <button
                  key={step.key}
                  onClick={() => setCurrentStep(step.key as any)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    currentStep === (step.key as any)
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{step.icon}</span>
                  <span className="font-medium">{step.label}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => exportToExcel('all')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
              >
                <span>📊</span>
                <span>Export All</span>
              </button>
              <div className="relative group">
                <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2">
                  <span>📥</span>
                  <span>Export</span>
                  <span>▼</span>
                </button>
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => exportToExcel('ingredients')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Export Ingredients
                    </button>
                    <button
                      onClick={() => exportToExcel('products')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Export Products
                    </button>
                    <button
                      onClick={() => exportToExcel('formulas')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Export Formulas
                    </button>
                    <button
                      onClick={() => exportToExcel('assessments')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Export Assessments
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="text-red-400">⚠️</div>
              <div className="ml-3 text-red-800">{error ? toReadableMessage(error) : null}</div>
                </div>
                </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex">
              <div className="text-green-400">✅</div>
              <div className="ml-3 text-green-800">{success}</div>
            </div>
              </div>
        )}

        {/* Overview Step */}
        {currentStep === 'overview' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-black mb-3">What this module does</h2>
              <p className="text-gray-800 mb-4">
                Perform end‑to‑end Toxicological Risk Assessment aligned with SCCS methodology. Define ingredients with NOAEL and Dermal Absorption (DA%),
                assemble formulas and validate totals, compute exposure (SED) per ingredient and overall Margin of Safety (MoS), then route assessments for review and generate a PDF report.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="font-semibold text-gray-900 mb-1">SED (Systemic Exposure Dose)</div>
                  <div className="text-sm text-gray-800">SED = (A × F × C × DA) / BW</div>
                  <ul className="mt-2 text-sm text-gray-700 list-disc pl-5">
                    <li>A: Amount used per application (mg/day)</li>
                    <li>F: Frequency (applications/day)</li>
                    <li>C: Concentration of ingredient (%)</li>
                    <li>DA: Dermal absorption fraction</li>
                    <li>BW: Body weight (kg)</li>
                  </ul>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="font-semibold text-gray-900 mb-1">MoS (Margin of Safety)</div>
                  <div className="text-sm text-gray-800">MoS = NOAEL / SED</div>
                  <ul className="mt-2 text-sm text-gray-700 list-disc pl-5">
                    <li>NOAEL: No Observed Adverse Effect Level (mg/kg/day)</li>
                    <li>Traffic‑light status: Green ≥ 100, Yellow 30–100, Red &lt; 30</li>
                    <li>Unknown values are shown when inputs are missing</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-black mb-3">How to use</h3>
              <ol className="list-decimal pl-5 text-sm text-gray-800 space-y-2">
                <li>Add <span className="font-medium">Ingredients</span> with NOAEL and DA% (CSV import supported).</li>
                <li>Create <span className="font-medium">Products</span> and associate their intended use.</li>
                <li>Build <span className="font-medium">Formulas</span>, validate concentrations, resolve conflicts.</li>
                <li>Create and calculate <span className="font-medium">Assessments</span>, then submit for review and export the PDF.</li>
              </ol>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button onClick={() => setCurrentStep('ingredients')} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Go to Ingredients</button>
                <button onClick={() => setCurrentStep('formulas')} className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Go to Formulas</button>
                <button onClick={() => setCurrentStep('assessments')} className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">Go to Assessments</button>
                <button onClick={() => exportToExcel('all')} className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm">Export All</button>
              </div>
            </div>
          </div>
        )}

        {/* Ingredients Step */}
        {currentStep === 'ingredients' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Create Ingredient */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-black">Add New Ingredient</h2>
                <button
                  onClick={() => setShowBulkImport(!showBulkImport)}
                  className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  {showBulkImport ? 'Hide' : 'Bulk Import'}
                </button>
            </div>
              
              {showBulkImport && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium mb-3">Bulk Import from CSV</h3>
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleBulkImportFile(file);
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                    <p className="text-xs text-gray-700">
                      CSV format: inci_name, cas_number, function (or name, cas, purpose)
                    </p>
                    {bulkImportData?.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Ready to import {bulkImportData?.length || 0} ingredients</span>
                          <button
                            onClick={processBulkImport}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {loading ? `Importing... ${importProgress}%` : 'Import All'}
                          </button>
          </div>
                        {importProgress > 0 && (
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${importProgress}%` }}
                            ></div>
        </div>
      )}
        </div>
                    )}
          </div>
        </div>
      )}

              {/* Formula Validation */}
              {showValidation && (validationErrors.length > 0 || validationWarnings.length > 0) && (
                <div className="mb-6 p-4 rounded-lg">
                  {validationErrors.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-base font-bold text-red-900 mb-2">❌ ERRORS (MUST FIX):</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        {validationErrors.map((error, index) => (
                          <li key={index} className="flex items-center">
                            <span className="text-red-500 mr-2">❌</span>
                            {error ? toReadableMessage(error) : null}
                          </li>
                        ))}
                  </ul>
                </div>
                  )}
                  {validationWarnings.length > 0 && (
                <div>
                      <h4 className="text-base font-bold text-yellow-900 mb-2">⚠️ WARNINGS:</h4>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        {validationWarnings.map((warning, index) => (
                          <li key={index} className="flex items-center">
                            <span className="text-yellow-500 mr-2">⚠️</span>
                            {warning ? toReadableMessage(warning) : null}
                          </li>
                        ))}
                  </ul>
                </div>
                  )}
              </div>
            )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-black mb-1">INCI Name *</label>
                  <input
                    type="text"
                    value={newIngredient.inci_name}
                    onChange={(e) => setNewIngredient({...newIngredient, inci_name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Niacinamide"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-black mb-1">CAS Number</label>
                  <input
                    type="text"
                    value={newIngredient.cas_number}
                    onChange={(e) => setNewIngredient({...newIngredient, cas_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 98-92-0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-enriches with PubChem data if provided</p>
                </div>
                <div>
                  <label className="block text-sm font-bold text-black mb-1">Function</label>
                  <input
                    type="text"
                    value={newIngredient.function}
                    onChange={(e) => setNewIngredient({...newIngredient, function: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Skin conditioning agent"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-black mb-1">NOAEL (mg/kg/day)</label>
                    <input
                      type="number"
                      value={newIngredient.noael_mg_kg_day}
                      onChange={(e) => setNewIngredient({...newIngredient, noael_mg_kg_day: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 500"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-black mb-1">Dermal Absorption %</label>
                    <input
                      type="number"
                      value={newIngredient.dermal_absorption_pct}
                      onChange={(e) => setNewIngredient({...newIngredient, dermal_absorption_pct: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 50"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-black mb-1">SCCS Reference</label>
                    <input
                      type="text"
                      value={newIngredient.sccs_ref}
                      onChange={(e) => setNewIngredient({ ...newIngredient, sccs_ref: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., SCCS/1576/16"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-black mb-1">Allergenicity</label>
                    <select
                      value={newIngredient.allergenicity}
                      onChange={(e) => setNewIngredient({ ...newIngredient, allergenicity: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select</option>
                      <option value="None">None</option>
                      <option value="Low">Low</option>
                      <option value="Moderate">Moderate</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-black mb-1">Toxicological Class</label>
                    <select
                      value={newIngredient.toxicological_class}
                      onChange={(e) => setNewIngredient({ ...newIngredient, toxicological_class: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select</option>
                      <option value="Safe">Safe</option>
                      <option value="Restricted">Restricted</option>
                      <option value="Banned">Banned</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={createIngredient}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Ingredient'}
          </button>
        </div>
      </div>

            {/* Ingredients List */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-black">Ingredients Library ({safeIngredients.length})</h2>
          <div className="flex gap-2">
                  <button
                    onClick={() => selectAllItems('ingredients', safeIngredients)}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => clearSelection('ingredients')}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                  >
                    Clear
                  </button>
                  {selectedItems.ingredients?.length > 0 && (
                    <button
                      onClick={() => setShowBatchOperations(true)}
                      className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                    >
                      Batch ({selectedItems.ingredients?.length || 0})
                    </button>
                  )}
          </div>
        </div>
              <div className="mb-4">
                <input
                  type="text"
                  value={ingredientSearch}
                  onChange={(e) => setIngredientSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search by INCI name or CAS..."
                />
          </div>
              <div className="max-h-96 overflow-y-auto">
                {filteredIngredients.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    {ingredientSearch ? 'No ingredients found' : 'No ingredients yet'}
        </div>
                ) : (
                  <div className="space-y-2">
                    {filteredIngredients.map((ingredient) => (
                      <div key={ingredient.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={selectedItems.ingredients.includes(ingredient.id)}
                              onChange={() => toggleItemSelection('ingredients', ingredient.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                <div>
                              <h3 className="font-medium text-gray-900">{ingredient.inci_name}</h3>
                              <p className="text-sm text-gray-700">
                                {ingredient.cas_number && `CAS: ${ingredient.cas_number}`}
                                {ingredient.function && ` • ${ingredient.function}`}
                              </p>
                              {ingredient.metadata_json && (
                                <p className="text-xs text-green-600 mt-1">✓ Enriched with PubChem data</p>
                              )}
                </div>
              </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500">ID: {ingredient.id}</div>
                            <div className="flex space-x-2 mt-1">
                              {!ingredient.metadata_json && ingredient.cas_number && (
                                <button
                                  onClick={() => enrichIngredient(ingredient.id, ingredient.cas_number!)}
                                  className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
                                >
                                  Enrich
                                </button>
                              )}
                              <button
                                onClick={() => getSubstitutionSuggestions(ingredient.id)}
                                className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded"
                                title="Find substitutes"
                              >
                                🔄
                              </button>
                              <button
                                onClick={() => openComments('ingredient', ingredient.id)}
                                className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                                title="Add comment"
                              >
                                💬
                              </button>
          </div>
                          </div>
                        </div>
                      </div>
                    ))}
        </div>
      )}
      </div>
          </div>
        </div>
      )}

        {/* Products Step */}
        {currentStep === 'products' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Create Product */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-black mb-4">Add New Product</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Anti-Aging Serum"
                  />
            </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
                  <select
                    value={newProduct.product_type}
                    onChange={(e) => setNewProduct({...newProduct, product_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="cosmetic">Cosmetic</option>
                    <option value="personal_care">Personal Care</option>
                    <option value="hygiene">Hygiene</option>
            </select>
              </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intended Use</label>
                  <input
                    type="text"
                    value={newProduct.intended_use}
                    onChange={(e) => setNewProduct({...newProduct, intended_use: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
            </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                  <select
                    value={newProduct.target_audience}
                    onChange={(e) => setNewProduct({...newProduct, target_audience: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Adults">Adults</option>
                    <option value="Children">Children</option>
                    <option value="All Ages">All Ages</option>
            </select>
          </div>
                <button
                  onClick={createProduct}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Product'}
          </button>
        </div>
      </div>

            {/* Products List */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-black mb-4">Products ({safeProducts.length})</h2>
              <div className="mb-4">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search products..."
                />
          </div>
              <div className="max-h-96 overflow-y-auto">
                {filteredProducts.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    {productSearch ? 'No products found' : 'No products yet'}
          </div>
                ) : (
                  <div className="space-y-2">
                    {filteredProducts.map((product) => (
                      <div key={product.id} className="border border-gray-200 rounded-lg p-3">
                        <h3 className="font-medium text-gray-900">{product.name}</h3>
                        <p className="text-sm text-gray-700">
                          {product.product_type} • {product.target_audience}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{product.intended_use}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
          </div>
        </div>
      )}

        {/* Formulas Step */}
        {currentStep === 'formulas' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Create Formula */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-black">Create Formula</h2>
          <div className="flex gap-2">
                  <button
                    onClick={() => setShowCostCalculator(!showCostCalculator)}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                  >
                    {showCostCalculator ? 'Hide Cost' : 'Show Cost'}
                  </button>
                  <button
                    onClick={detectConflicts}
                    className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
                  >
                    Check Conflicts
                  </button>
                  <button
                    onClick={checkComplianceAlerts}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                  >
                    Check Compliance
                  </button>
                  <button
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                  >
                    {showTemplates ? 'Hide Templates' : 'Use Template'}
                  </button>
          </div>
        </div>

              {showTemplates && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium mb-3">Formula Templates</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(formulaTemplates).map(([key, template]) => (
                      <div key={key} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-100 cursor-pointer"
                           onClick={() => applyTemplate(key)}>
                        <h4 className="font-medium text-gray-900">{template.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                        <div className="text-xs text-gray-500">
                          {template.items?.length || 0} ingredients • {(template.items?.reduce((sum, item) => sum + item.concentration_pct, 0) || 0).toFixed(1)}% total
          </div>
          </div>
                    ))}
          </div>
          </div>
        )}
              
              {showCostCalculator && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Cost Calculator</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <label className="text-sm font-semibold text-gray-900">Batch Size (grams):</label>
                      <input
                        type="number"
                        value={batchSize}
                        onChange={(e) => setBatchSize(Number(e.target.value))}
                        className="w-24 border border-gray-300 rounded px-2 py-1"
                        min="1"
                      />
      </div>

                    {safeFormulaItems.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-gray-900">Cost Breakdown:</div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {costBreakdown.map((item, index) => (
                            <div key={index} className="flex justify-between text-xs bg-white p-2 rounded">
                              <span className="text-gray-900">{item.ingredient} ({item.concentration}%)</span>
                              <span className="text-gray-900">${item.totalCost.toFixed(2)}</span>
          </div>
                          ))}
        </div>
                        <div className="border-t pt-2 flex justify-between font-semibold text-gray-900">
                          <span>Total Cost for {batchSize}g batch:</span>
                          <span className="text-green-700">${totalCost.toFixed(2)}</span>
      </div>
                        <div className="text-xs text-gray-900">
                          Cost per gram: ${(totalCost / batchSize).toFixed(4)}
      </div>
          </div>
                    )}
                    
                    {safeFormulaItems.length === 0 && (
                      <div className="text-sm text-gray-700">Add ingredients to see cost breakdown</div>
            )}
          </div>
          </div>
              )}
              
              {/* Conflicts Display */}
              {showConflicts && conflicts.length > 0 && (
                <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h3 className="font-medium text-orange-800 mb-3">⚠️ Ingredient Conflicts Detected</h3>
                  <div className="space-y-3">
                    {conflicts.map((conflict, index) => (
                      <div key={index} className="bg-white p-3 rounded border-l-4 border-orange-500">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                conflict.severity === 'high' ? 'bg-red-100 text-red-800' :
                                conflict.severity === 'medium' ? 'bg-orange-100 text-orange-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {conflict.severity.toUpperCase()}
                              </span>
                              {conflict.ingredient && (
                                <span className="font-medium">{conflict.ingredient}</span>
                              )}
            </div>
                            <p className="text-sm text-gray-700 mb-1">{conflict.reason}</p>
                            {conflict.conflictingWith && (
                              <p className="text-xs text-gray-600">
                                Conflicts with: {conflict.conflictingWith.join(', ')}
                              </p>
                            )}
                            <p className="text-xs text-blue-600 mt-1 font-medium">
                              💡 {conflict.recommendation}
                            </p>
              </div>
            </div>
          </div>
                    ))}
          </div>
                  <button
                    onClick={() => setShowConflicts(false)}
                    className="mt-3 text-sm text-orange-600 hover:text-orange-800"
                  >
                    Dismiss
                  </button>
        </div>
      )}

              {/* Compliance Alerts Display */}
              {showComplianceAlerts && complianceAlerts.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="font-medium text-red-800 mb-3">🚨 Compliance Alerts</h3>
                  <div className="space-y-3">
                    {complianceAlerts.map((alert, index) => (
                      <div key={index} className={`bg-white p-3 rounded border-l-4 ${
                        alert.severity === 'critical' ? 'border-red-500' :
                        alert.severity === 'error' ? 'border-red-400' :
                        alert.severity === 'warning' ? 'border-yellow-500' :
                        'border-blue-500'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                alert.severity === 'error' ? 'bg-red-100 text-red-800' :
                                alert.severity === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {alert.severity.toUpperCase()}
                              </span>
                              {alert.ingredient && (
                                <span className="font-medium">{alert.ingredient}</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mb-1">{alert.message}</p>
                            {alert.regulation && (
                              <p className="text-xs text-gray-600 mb-1">
                                Regulation: {alert.regulation}
                              </p>
                            )}
                            <p className="text-xs text-blue-600 font-medium">
                              💡 {alert.action}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
          </div>
                  <button
                    onClick={() => setShowComplianceAlerts(false)}
                    className="mt-3 text-sm text-red-600 hover:text-red-800"
                  >
                    Dismiss
                  </button>
        </div>
      )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Formula Name *</label>
                  <input
                    type="text"
                    value={newFormula.name}
                    onChange={(e) => setNewFormula({...newFormula, name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Anti-Aging Serum Formula"
                  />
          </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product (Optional)</label>
                  <select
                    value={newFormula.product_id}
                    onChange={(e) => setNewFormula({...newFormula, product_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a product...</option>
                    {safeProducts.map(product => (
                      <option key={product.id} value={product.id.toString()}>{product.name}</option>
              ))}
            </select>
          </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Ingredients</label>
                    <button
                      onClick={addFormulaItem}
                      className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded"
                    >
                      + Add Ingredient
                    </button>
        </div>

                  {safeFormulaItems.length === 0 ? (
                    <div className="text-center text-gray-500 py-4 border-2 border-dashed border-gray-300 rounded-lg">
                      No ingredients added yet
          </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {safeFormulaItems.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 border border-gray-200 rounded">
                          <select
                            value={item.ingredient_id}
                            onChange={(e) => updateFormulaItem(index, 'ingredient_id', parseInt(e.target.value))}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            <option value={0}>Select ingredient...</option>
                            {safeIngredients.map(ing => (
                              <option key={ing.id} value={ing.id}>{ing.inci_name}</option>
                            ))}
            </select>
                          {item.ingredient_id === 0 && (
                            <input
                              type="text"
                              value={item.temp_name || ''}
                              onChange={(e) => updateFormulaItemName(index, e.target.value)}
                              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                              placeholder="Enter ingredient name"
                            />
                          )}
                          <input
                            type="number"
                            value={item.concentration_pct}
                            onChange={(e) => updateFormulaItem(index, 'concentration_pct', parseFloat(e.target.value) || 0)}
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="%"
                            min="0"
                            max="100"
                            step="0.1"
                          />
                          <input
                            type="number"
                            value={item.override_da_pct ?? ''}
                            onChange={(e) => updateFormulaItem(index, 'override_da_pct', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
                            className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="DA % (opt)"
                            min="0"
                            max="100"
                            step="0.1"
                            title="Optional: Override Dermal Absorption % for this formula item"
                          />
                          <input
                            type="number"
                            value={item.override_noael_mg_kg_day ?? ''}
                            onChange={(e) => updateFormulaItem(index, 'override_noael_mg_kg_day', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="NOAEL (opt)"
                            min="0"
                            step="0.1"
                            title="Optional: Override NOAEL (mg/kg/day) for this formula item"
                          />
                          {(() => {
                            const sel = safeIngredients.find((ing: any) => ing.id === item.ingredient_id);
                            const txt = ((sel?.inci_name || (item as any).temp_name || item.ingredient_name || '') + ' ' + (sel?.cas_number || '') + ' ' + (item.function || '')).toLowerCase();
                            return /ptfe|perfluoro|polyfluoro/.test(txt);
                          })() && (
                            <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800 border border-red-200" title="PFAS-like term detected">
                              PFAS?
                            </span>
                          )}
                          {showCostCalculator && (
                            <input
                              type="number"
                              value={ingredientCosts[item.ingredient_id] || ''}
                              onChange={(e) => setIngredientCosts({
                                ...ingredientCosts,
                                [item.ingredient_id]: parseFloat(e.target.value) || 0
                              })}
                              className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                              placeholder="$/g"
                              min="0"
                              step="0.01"
                            />
                          )}
                          <button
                            onClick={() => openComments('formula_item', index)}
                            className="text-blue-600 hover:text-blue-800 mr-2"
                            title="Add comment"
                          >
                            💬
                          </button>
                          <button
                            onClick={() => removeFormulaItem(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
          </div>
                      ))}
        </div>
          )}
      </div>

                <button
                  onClick={createFormula}
                  disabled={loading || !isFormulaValid}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Formula'}
                </button>
                {!isFormulaValid && (
                  <p className="text-xs text-red-600 mt-1">
                    Fix errors above to create formula
                  </p>
                )}
          </div>
          </div>

            {/* Formulas List */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-black">Formulas ({safeFormulas.length})</h2>
                <div className="flex gap-2">
                  {(selectedFormulas?.length || 0) >= 2 && (
                    <button
                      onClick={compareFormulas}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Compare ({selectedFormulas?.length || 0})
                    </button>
                  )}
          </div>
          </div>
              <div className="max-h-96 overflow-y-auto">
                {safeFormulas.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">No formulas yet</div>
                ) : (
                  <div className="space-y-2">
                    {safeFormulas.map((formula) => (
                      <div key={formula.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedFormulas.includes(formula.id)}
                            onChange={() => toggleFormulaSelection(formula.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{formula.name}</h3>
                            <p className="text-sm text-gray-600">
                              {formula.items?.length || 0} ingredient{(formula.items?.length || 0) !== 1 ? 's' : ''}
                            </p>
                            <div className="mt-2 space-y-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <select
                                  className="text-sm border border-gray-300 rounded px-2 py-1"
                                  value={(assessmentOptions[formula.id]?.category) || 'LEAVE_ON_FACE'}
                                  onChange={(e) => setAssessmentOptions({
                                    ...assessmentOptions,
                                    [formula.id]: { category: e.target.value, notes: assessmentOptions[formula.id]?.notes || '' }
                                  })}
                                  title="Product Category"
                                >
                                  <option value="LEAVE_ON_FACE">LEAVE_ON_FACE</option>
                                  {categories.map((c) => (
                                    <option key={c.id} value={c.code}>{c.code}</option>
                                  ))}
                                </select>
                                <input
                                  type="text"
                                  className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded px-2 py-1"
                                  placeholder="Notes (optional)"
                                  value={assessmentOptions[formula.id]?.notes || ''}
                                  onChange={(e) => setAssessmentOptions({
                                    ...assessmentOptions,
                                    [formula.id]: { category: assessmentOptions[formula.id]?.category || 'LEAVE_ON_FACE', notes: e.target.value }
                                  })}
                                />
                                <button
                                  onClick={() => createAssessment(formula.id)}
                                  className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded"
                                >
                                  Create Assessment
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const res = await fetch(`http://localhost:8002/tra/formulas/${formula.id}/pfas-check?jurisdictions=CA,FR`, {
                                        method: 'GET',
                                        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                                      });
                                      if (!res.ok) throw new Error('PFAS check failed');
                                      const data = await res.json();
                                      const msg = (data.jurisdictions || []).map((j: any) => `${j.name}: ${j.status}`).join(' | ') || 'No data';
                                      showSuccess(`PFAS Check -> ${msg}`);
                                    } catch (e) {
                                      showError('PFAS check failed');
                                    }
                                  }}
                                  className="text-sm bg-red-100 text-red-700 px-3 py-1 rounded"
                                  title="PFAS compliance (CA, FR)"
                                >
                                  PFAS Check
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
          </div>
                  )}
        </div>
              </div>
              </div>
        )}

        {/* Advanced Reporting Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">📊 Advanced Reporting</h2>
            <button
              onClick={() => setShowAdvancedReporting(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Generate Reports
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => generateAdvancedReport('summary')}
              className="p-3 bg-blue-50 rounded-lg hover:bg-blue-100 text-left"
            >
              <div className="font-medium text-blue-900">Summary Report</div>
              <div className="text-sm text-blue-600">Overview of all data</div>
            </button>
            <button
              onClick={() => generateAdvancedReport('ingredients')}
              className="p-3 bg-green-50 rounded-lg hover:bg-green-100 text-left"
            >
              <div className="font-medium text-green-900">Ingredients Analysis</div>
              <div className="text-sm text-green-600">Function & enrichment stats</div>
            </button>
            <button
              onClick={() => generateAdvancedReport('formulas')}
              className="p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 text-left"
            >
              <div className="font-medium text-yellow-900">Formula Analysis</div>
              <div className="text-sm text-yellow-600">Complexity & concentration</div>
            </button>
            <button
              onClick={() => generateAdvancedReport('compliance')}
              className="p-3 bg-red-50 rounded-lg hover:bg-red-100 text-left"
            >
              <div className="font-medium text-red-900">Compliance Report</div>
              <div className="text-sm text-red-600">Issues & recommendations</div>
            </button>
          </div>
        </div>

        {/* Assessments Step */}
        {currentStep === 'assessments' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-black mb-4">Safety Assessments ({safeAssessments.length})</h2>
            <div className="max-h-96 overflow-y-auto">
              {safeAssessments.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No assessments yet</div>
              ) : (
                <div className="space-y-2">
                  {safeAssessments.map((assessment) => (
                    <div key={assessment.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-gray-900">
                            Assessment #{assessment.id}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Formula: {assessment.formula_name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Created: {assessment.date_created || assessment.created_at ? 
                              new Date(assessment.date_created || assessment.created_at || '').toLocaleDateString() : 
                              'Unknown'}
                          </p>
                          <span className={`inline-block px-2 py-1 text-xs rounded mt-2 ${
                            (assessment.approval_status === 'approved') ? 'bg-green-100 text-green-800' :
                            (assessment.approval_status === 'submitted' || assessment.approval_status === 'calculated') ? 'bg-yellow-100 text-yellow-800' :
                            (assessment.approval_status === 'rejected') ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {assessment.approval_status || assessment.status || 'draft'}
                          </span>
                          </div>
                        <div className="flex space-x-2">
                          {(assessment.approval_status === 'draft' || assessment.status === 'draft') && (
                            <button
                              onClick={() => calculateAssessment(assessment.id)}
                              className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded"
                            >
                              Calculate
                            </button>
                          )}
                          {(assessment.approval_status === 'calculated' || assessment.status === 'calculated') && (
                            <button
                              onClick={() => openReviewDialog(assessment.id, 'submit')}
                              className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded"
                            >
                              Submit for Review
                            </button>
                          )}
                          {(assessment.approval_status === 'submitted' || assessment.status === 'submitted') && (
                            <>
                              <button
                                onClick={() => openReviewDialog(assessment.id, 'approve')}
                                className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => openReviewDialog(assessment.id, 'reject')}
                                className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {((assessment.status && assessment.status !== 'draft') || (assessment.approval_status && assessment.approval_status !== 'draft')) && (
                            <>
                              <button
                                onClick={() => traApi.openReport(assessment.id)}
                                className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200"
                                title="View PDF Report"
                              >
                                📄 PDF
                              </button>
                              <button
                                onClick={() => traApi.exportXlsx(assessment.id)}
                                className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200"
                                title="Export to Excel"
                              >
                                📊 Excel
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const res = await fetch(`http://localhost:8002/tra/assessments/${assessment.id}/report`, {
                                      method: 'GET',
                                      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                                    });
                                    if (!res.ok) throw new Error('Failed to download report');
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `TRA_Assessment_${assessment.id}_Report.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    URL.revokeObjectURL(url);
                                    showSuccess('Report downloaded successfully');
                                  } catch (error) {
                                    showError('Failed to download report');
                                  }
                                }}
                                className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded hover:bg-purple-200"
                                title="Download PDF Report"
                              >
                                💾 Download
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => toggleAssessmentResults(assessment.id)}
                            className="text-sm bg-gray-100 text-gray-800 px-3 py-1 rounded"
                          >
                            {openAssessmentResults[assessment.id] ? 'Hide Results' : 'Show Results'}
                          </button>
                          </div>
              </div>
                      {/* Assessment meta */}
                      <div className="mt-2 text-xs text-gray-600">
                        <span className={`inline-block px-2 py-1 rounded mr-2 ${
                          (assessment.mos_status === 'green') ? 'bg-green-100 text-green-800' :
                          (assessment.mos_status === 'yellow') ? 'bg-yellow-100 text-yellow-800' :
                          (assessment.mos_status === 'red') ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          MoS: {assessment.mos_status || 'unknown'}
                        </span>
                        <span className={`inline-block px-2 py-1 rounded ${
                          (assessment.approval_status === 'approved') ? 'bg-green-100 text-green-800' :
                          (assessment.approval_status === 'submitted' || assessment.approval_status === 'calculated') ? 'bg-yellow-100 text-yellow-800' :
                          (assessment.approval_status === 'rejected') ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          Status: {assessment.approval_status || assessment.status || 'draft'}
                        </span>
                      </div>

                      {/* Results table */}
                      {openAssessmentResults[assessment.id] && (
                        <div className="mt-3 border-t pt-2">
                          {(!resultsByAssessment[assessment.id] || resultsByAssessment[assessment.id].length === 0) ? (
                            <div className="text-xs text-gray-500">No results yet. Click Calculate or refresh.</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm bg-white border border-gray-300 rounded">
                                <thead className="bg-gray-50">
                                  <tr className="text-gray-900">
                                    <th className="text-left py-2 px-3 font-semibold border-b border-gray-300">Ingredient</th>
                                    <th className="text-right py-2 px-3 font-semibold border-b border-gray-300">SED</th>
                                    <th className="text-right py-2 px-3 font-semibold border-b border-gray-300">NOAEL</th>
                                    <th className="text-right py-2 px-3 font-semibold border-b border-gray-300">MoS</th>
                                    <th className="text-left py-2 px-3 font-semibold border-b border-gray-300">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resultsByAssessment[assessment.id].map((r: any, idx: number) => (
                                    <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                                      <td className="py-2 px-3 font-medium text-gray-900">{r.ingredient_name}</td>
                                      <td className="py-2 px-3 text-right font-mono text-gray-900">{r.sed_mg_kg_day == null ? '-' : r.sed_mg_kg_day.toFixed(4)}</td>
                                      <td className="py-2 px-3 text-right font-mono text-gray-900">{r.noael_mg_kg_day == null ? '-' : r.noael_mg_kg_day.toFixed(2)}</td>
                                      <td className="py-2 px-3 text-right font-mono text-gray-900">{r.mos == null ? '-' : r.mos.toFixed(2)}</td>
                                      <td className="py-2 px-3">
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                          r.status === 'green' ? 'bg-green-100 text-green-900 border border-green-300' :
                                          r.status === 'yellow' ? 'bg-yellow-100 text-yellow-900 border border-yellow-300' :
                                          r.status === 'red' ? 'bg-red-100 text-red-900 border border-red-300' :
                                          'bg-gray-100 text-gray-900 border border-gray-300'
                                        }`}>
                                          {r.status || 'unknown'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          )}

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-700">Loading...</span>
          </div>
            </div>
          )}
          </div>

      {/* Comments Modal */}
      {showComments && commentTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-96 overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Comments - {commentTarget.type} #{commentTarget.id}
              </h3>
              <button
                onClick={() => setShowComments(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4 max-h-64 overflow-y-auto">
              {getComments(commentTarget.type, commentTarget.id).map((comment: any) => (
                <div key={comment.id} className="border-l-4 border-blue-500 pl-3 py-2">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-medium text-gray-900">{comment.author}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(comment.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1">{comment.text}</p>
                        </div>
                      ))}
              
              {getComments(commentTarget.type, commentTarget.id).length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">No comments yet</p>
              )}
          </div>
            
            <div className="mt-4 flex space-x-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addComment(commentTarget.type, commentTarget.id, newComment);
                  }
                }}
              />
              <button
                onClick={() => addComment(commentTarget.type, commentTarget.id, newComment)}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Add
              </button>
        </div>
          </div>
                    </div>
                  )}

      {/* Batch Operations Modal */}
      {showBatchOperations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Batch Operations</h3>
              <button
                onClick={() => setShowBatchOperations(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
                </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Operation</label>
                <select
                  value={batchOperation}
                  onChange={(e) => setBatchOperation(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Choose operation...</option>
                  <option value="export">Export Selected Items</option>
                  <option value="enrich">Enrich Ingredients (if applicable)</option>
                </select>
              </div>
              
              <div className="text-sm text-gray-600">
                <p>Selected items:</p>
                <ul className="mt-1 space-y-1">
                  {Object.entries(selectedItems).map(([type, ids]) => (
                    ids.length > 0 && (
                      <li key={type} className="capitalize">
                        {type}: {ids.length} items
                      </li>
                    )
                  ))}
                </ul>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowBatchOperations(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={executeBatchOperation}
                  disabled={!batchOperation || loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Execute'}
                </button>
                          </div>
                          </div>
          </div>
        </div>
      )}

      {/* Formula Comparison Modal */}
      {showFormulaComparison && comparisonResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Formula Comparison</h3>
              <button
                onClick={() => setShowFormulaComparison(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4 max-h-80 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {comparisonResults.formulas.map((formula: any) => (
                  <div key={formula.id} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">{formula.name}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Concentration:</span>
                        <span className="font-medium">
                          {comparisonResults.totalConcentration[formula.id]?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Estimated Cost:</span>
                        <span className="font-medium">
                          ${comparisonResults.costComparison[formula.id]?.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ingredients:</span>
                        <span className="font-medium">{formula.items?.length || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Comparison Summary</h4>
                <div className="text-sm text-gray-600">
                  <p>• Common ingredients: {comparisonResults.commonIngredients.length}</p>
                  <p>• Cost range: ${Math.min(...(Object.values(comparisonResults.costComparison) as number[])).toFixed(2)} - ${Math.max(...(Object.values(comparisonResults.costComparison) as number[])).toFixed(2)}</p>
                  <p>• Concentration range: {Math.min(...(Object.values(comparisonResults.totalConcentration) as number[])).toFixed(1)}% - {Math.max(...(Object.values(comparisonResults.totalConcentration) as number[])).toFixed(1)}%</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowFormulaComparison(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
              </div>
            </div>
          )}

      {/* Substitution Suggestions Modal */}
      {showSubstitutionSuggestions && substitutionSuggestions.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">🔄 Ingredient Substitution Suggestions</h3>
              <button
                onClick={() => setShowSubstitutionSuggestions(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
        </div>
            
            <div className="space-y-4 max-h-80 overflow-y-auto">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Original:</strong> {substitutionSuggestions[0]?.originalIngredient} 
                  {substitutionSuggestions[0]?.originalFunction && ` (${substitutionSuggestions[0].originalFunction})`}
                </p>
      </div>

              <div className="space-y-3">
                {substitutionSuggestions.map((suggestion, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{suggestion.name}</h4>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          suggestion.compatibility >= 90 ? 'bg-green-100 text-green-800' :
                          suggestion.compatibility >= 80 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {suggestion.compatibility}% match
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-700 mb-2">{suggestion.reason}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-600">Cost:</span>
                        <span className={`font-medium ${
                          suggestion.costImpact.includes('Lower') ? 'text-green-600' :
                          suggestion.costImpact.includes('Higher') ? 'text-red-600' :
                          'text-gray-600'
                        }`}>
                          {suggestion.costImpact}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-600">Availability:</span>
                        <span className={`font-medium ${
                          suggestion.availability.includes('Readily') ? 'text-green-600' : 'text-orange-600'
                        }`}>
                          {suggestion.availability}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-600">Compatibility:</span>
                        <span className="font-medium text-gray-600">
                          {suggestion.compatibility}%
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex space-x-2">
                      <button
                        onClick={() => {
                          showSuccess(`Substitution suggestion: ${suggestion.name}`);
                          setShowSubstitutionSuggestions(false);
                        }}
                        className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200"
                      >
                        Use This
                      </button>
                      <button
                        onClick={() => {
                          showSuccess(`Added ${suggestion.name} to research list`);
                        }}
                        className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200"
                      >
                        Research
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowSubstitutionSuggestions(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showComments && commentTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {`Comments - ${commentTarget.type} #${commentTarget.id}`}
              </h3>
              <button
                onClick={() => setShowComments(false)}
                className="text-gray-600 hover:text-gray-800"
                aria-label="Close comments"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {getComments(commentTarget.type, commentTarget.id).length === 0 ? (
                <div className="text-sm text-gray-700">No comments yet</div>
              ) : (
                <ul className="space-y-2">
                  {getComments(commentTarget.type, commentTarget.id).map((c: any) => (
                    <li key={c.id} className="bg-gray-50 border border-gray-200 rounded p-2">
                      <div className="text-sm text-gray-900">{c.text}</div>
                      <div className="text-xs text-gray-600 mt-1">{new Date(c.timestamp).toLocaleString()} • {c.author}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-gray-900 placeholder-gray-500"
              />
              <button
                onClick={() => commentTarget && addComment(commentTarget.type, commentTarget.id, newComment)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Action Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold text-gray-900">
                {reviewAction === 'submit' ? 'Submit for Review' : reviewAction === 'approve' ? 'Approve Assessment' : 'Reject Assessment'}
              </h3>
              <button onClick={() => setShowReviewModal(false)} className="text-gray-600 hover:text-gray-800" aria-label="Close">✕</button>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900">Comment</label>
              <textarea
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                rows={3}
                placeholder={reviewAction === 'submit' ? 'Ready for toxicologist review' : reviewAction === 'approve' ? 'Approved for production' : 'Reason for rejection'}
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowReviewModal(false)} className="px-3 py-1 text-gray-700 bg-gray-100 rounded">Cancel</button>
              <button onClick={performReviewAction} className="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700">Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Reporting Modal */}
      {showAdvancedReporting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-96 overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">📊 Advanced Reporting Dashboard</h3>
              <div className="flex space-x-2">
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1"
                >
                  <option value="summary">Summary Report</option>
                  <option value="ingredients">Ingredients Analysis</option>
                  <option value="formulas">Formula Analysis</option>
                  <option value="compliance">Compliance Report</option>
                  <option value="cost">Cost Analysis</option>
            </select>
                <button
                  onClick={() => generateAdvancedReport(reportType)}
                  className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Generate
                </button>
                <button
                  onClick={() => setShowAdvancedReporting(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
          </div>
            </div>
            
            {reportData && (
              <div className="space-y-4 max-h-80 overflow-y-auto">
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-900">
                    <strong>Report Type:</strong> {reportData.type} | 
                    <strong> Generated:</strong> {new Date(reportData.generatedAt).toLocaleString()}
                  </p>
                </div>
                
                {reportData.summary && Object.keys(reportData.summary).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(reportData.summary).map(([key, value]) => (
                      <div key={key} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <div className="text-sm text-blue-800 font-medium capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                        <div className="text-xl font-semibold text-blue-900">{String(value)}</div>
                      </div>
                    ))}
            </div>
          )}
                
                {reportData.details && Object.keys(reportData.details).length > 0 && (
                  <div className="space-y-4">
                    {Object.entries(reportData.details).map(([key, value]) => (
                      <div key={key} className="border border-gray-300 rounded-lg p-4 bg-white">
                        <h4 className="font-semibold text-gray-900 mb-2 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </h4>
                        {Array.isArray(value) ? (
                          <div className="space-y-2">
                            {value.map((item, index) => (
                              <div key={index} className="text-sm text-gray-900">
                                {typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)}
        </div>
                            ))}
                          </div>
                        ) : typeof value === 'object' && value !== null ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {Object.entries(value as Record<string, any>).map(([subKey, subValue]) => (
                              <div key={subKey} className="flex justify-between">
                                <span className="text-gray-900 font-medium">{subKey}:</span>
                                <span className="font-semibold text-gray-900">{String(subValue)}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900">{String(value || '')}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {reportData.recommendations && reportData.recommendations.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">Recommendations</h4>
                    <ul className="space-y-1">
                      {reportData.recommendations.map((rec: string, index: number) => (
                        <li key={index} className="text-sm text-yellow-900">• {rec}</li>
                      ))}
                    </ul>
          </div>
                )}
        </div>
            )}
            
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowAdvancedReporting(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
      </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TRAHome;